import { Badge, Button, Group, Stack, Text } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@prooffleet/client';
import { severityColor } from '@prooffleet/ui';
import { EmptyState, PageHeader } from '../components/Page.js';

interface NotificationRow {
  _id: string;
  event: string;
  severity: string;
  title: string;
  body: string;
  action: string;
  unitNumber: string | null;
  occurrences: number;
  escalationLevel: number;
  readAt: string | null;
  createdAt: string;
  deliveries: { channel: string; status: string; attemptedAt: string; error: string | null }[];
}

/**
 * Центр уведомлений.
 *
 * Показывается и журнал доставки: «доставлено тогда-то, открыто тогда-то» —
 * это часть доказательной базы, а не отладочная информация.
 */
export function Notifications(): React.JSX.Element {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api<{ notifications: NotificationRow[]; unread: number }>('/notifications'),
    refetchInterval: 20_000,
  });

  const markRead = useMutation({
    mutationFn: async (ids: string[]) =>
      api('/notifications/read', { method: 'POST', body: { notificationIds: ids } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const rows = data?.notifications ?? [];
  const unread = rows.filter((r) => r.readAt === null);

  return (
    <Stack gap={0}>
      <PageHeader
        title="Notifications"
        count={rows.length}
        hint="Delivery times stay on the record: this log is what shows the responsible person was told."
        action={
          unread.length > 0 ? (
            <Button
              variant="default"
              loading={markRead.isPending}
              onClick={() => markRead.mutate(unread.map((r) => r._id))}
            >
              Mark all read ({unread.length})
            </Button>
          ) : undefined
        }
      />

      {rows.length === 0 && (
        <EmptyState title="No notifications" hint="Alerts about defects and missed inspections land here." />
      )}

      <Stack gap={0}>
        {rows.map((row) => (
          <div
            key={row._id}
            style={{
              borderBottom: '1px solid var(--pf-line)',
              padding: '14px 16px',
              // Прочитанное приглушается, но не прячется: журнал должен быть полным.
              opacity: row.readAt === null ? 1 : 0.62,
            }}
          >
            <Group justify="space-between" align="flex-start" wrap="nowrap" gap="lg">
              <div style={{ minWidth: 0 }}>
                <Group gap={8} align="baseline">
                  {row.readAt === null && (
                    <span
                      aria-hidden
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: 999,
                        background: `var(--mantine-color-${severityColor(row.severity)}-6)`,
                        flex: '0 0 auto',
                      }}
                    />
                  )}
                  <Text fw={640} fz="sm">
                    {row.title}
                  </Text>
                  {row.occurrences > 1 && <Badge size="sm">×{row.occurrences}</Badge>}
                  {row.escalationLevel > 0 && (
                    <Badge size="sm" color="red">
                      escalation {row.escalationLevel}
                    </Badge>
                  )}
                </Group>
                <Text fz="sm" c="gray.7" mt={2}>
                  {row.body}
                </Text>

                <Group gap={6} mt={6}>
                  {row.deliveries.map((delivery) => (
                    <Badge
                      key={delivery.channel}
                      size="xs"
                      variant="outline"
                      color={
                        // Доставлено — ожидаемый исход, цветом выделяется только сбой.
                        delivery.status === 'failed' ? 'red' : 'gray'
                      }
                    >
                      {delivery.channel}: {delivery.status}
                    </Badge>
                  ))}
                </Group>

                <Text className="pf-mono" mt={5}>
                  {new Date(row.createdAt).toLocaleString()}
                  {row.readAt !== null && ` · opened ${new Date(row.readAt).toLocaleTimeString()}`}
                </Text>
              </div>

              <Group gap={8} wrap="nowrap" style={{ flex: '0 0 auto' }}>
                <Badge color={severityColor(row.severity)} size="sm">
                  {row.severity}
                </Badge>
                {row.readAt === null && (
                  <Button size="compact-xs" variant="default" onClick={() => markRead.mutate([row._id])}>
                    Mark read
                  </Button>
                )}
              </Group>
            </Group>
          </div>
        ))}
      </Stack>
    </Stack>
  );
}
